import { BookOpen, CalendarDays, FileText, ShieldCheck } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { Footer } from '../components/Footer';
import { Layout } from '../components/Layout';

const timeline = [
  {
    year: '2014',
    label: 'Old-regime threshold reset',
    regime: 'Old regime',
    details: [
      'Basic exemption for individuals below 60 moved from Rs 2L to Rs 2.5L.',
      'Senior citizen basic exemption moved to Rs 3L.',
      'Section 80C limit moved to Rs 1.5L and self-occupied home-loan interest limit moved to Rs 2L.',
    ],
  },
  {
    year: '2017',
    label: 'Lower first slab under old regime',
    regime: 'Old regime',
    details: [
      'Tax rate for the Rs 2.5L-Rs 5L slab reduced from 10% to 5%.',
      'A 10% surcharge was added for income above Rs 50L and up to Rs 1Cr.',
      'The old regime kept deduction-heavy planning as its main design.',
    ],
  },
  {
    year: '2018',
    label: 'Standard deduction and 4% cess',
    regime: 'Old regime',
    details: [
      'Standard deduction returned for salary and pension income at Rs 40,000.',
      'Health & Education Cess at 4% replaced the earlier 3% education cess structure.',
      'Equity long-term capital gains tax under section 112A became relevant for non-salary income.',
    ],
  },
  {
    year: '2019',
    label: 'Rebate and standard deduction expanded',
    regime: 'Old regime',
    details: [
      'Section 87A rebate made resident individuals with taxable income up to Rs 5L effectively tax-free.',
      'Salary standard deduction increased from Rs 40,000 to Rs 50,000.',
      'Higher surcharge tiers for very high incomes were added under the old-regime framework.',
    ],
  },
  {
    year: '2020',
    label: 'New tax regime introduced',
    regime: 'New regime',
    details: [
      'Section 115BAC created an optional lower-rate regime for individuals and HUFs.',
      'The tradeoff was fewer deductions and exemptions compared with the old regime.',
      'The original new-regime slabs were 0%, 5%, 10%, 15%, 20%, 25%, and 30% across wider income bands.',
    ],
  },
  {
    year: '2023',
    label: 'New regime made the default',
    regime: 'New regime',
    details: [
      'The new regime became the default tax regime, while taxpayers could still opt for the old regime.',
      'Section 87A relief under the new regime moved from Rs 5L to Rs 7L taxable income.',
      'New-regime slabs changed to Rs 0-3L nil, Rs 3-6L at 5%, Rs 6-9L at 10%, Rs 9-12L at 15%, Rs 12-15L at 20%, and above Rs 15L at 30%.',
      'The highest surcharge under the new regime was capped at 25%.',
    ],
  },
  {
    year: '2024',
    label: 'July 2024 new-regime revision',
    regime: 'New regime',
    details: [
      'New-regime standard deduction increased from Rs 50,000 to Rs 75,000.',
      'New slabs became Rs 0-3L nil, Rs 3-7L at 5%, Rs 7-10L at 10%, Rs 10-12L at 15%, Rs 12-15L at 20%, and above Rs 15L at 30%.',
      'Family pension deduction under the new regime increased from Rs 15,000 to Rs 25,000.',
      'Employer NPS deduction under the new regime increased from 10% to 14% of salary.',
      'Capital gains and TDS changes were also introduced outside the salary-slab structure.',
    ],
  },
  {
    year: '2025',
    label: 'Large new-regime reset',
    regime: 'New regime',
    details: [
      'New-regime nil slab moved to Rs 4L and the top 30% slab moved above Rs 24L.',
      'Section 87A threshold under the new regime moved to Rs 12L taxable income, with maximum rebate of Rs 60,000.',
      'For salaried users, Rs 75,000 standard deduction means regular salary income up to Rs 12.75L can be tax-free if eligible.',
      'Updated-return filing window moved from 24 months to 48 months.',
      'TDS/TCS rationalisation included higher thresholds for senior citizen interest and rent TDS.',
    ],
  },
  {
    year: '2026',
    label: 'Income-tax Act, 2025 framework',
    regime: 'Current framework',
    details: [
      'Tax Year 2026-27 uses the Income-tax Act, 2025 framework from April 1, 2026.',
      'Budget 2026 carried forward the 2025 new-regime slabs, rebate, standard deduction, and marginal-relief structure.',
      'Health & Education Cess remains 4% on income-tax after surcharge and relief adjustments.',
    ],
  },
];

const regimeSnapshots = [
  {
    title: 'Old Regime',
    period: 'Modern baseline after 2014, still available',
    points: [
      'Deduction-led system with Section 80C, 80D, HRA, home-loan interest, NPS and other exemptions.',
      'Common below-60 slab structure: Rs 0-2.5L nil, Rs 2.5-5L at 5%, Rs 5-10L at 20%, above Rs 10L at 30%.',
      'Standard deduction is Rs 50,000 for salaried taxpayers.',
      'Section 87A continues separately for eligible resident individuals up to Rs 5L taxable income.',
    ],
  },
  {
    title: 'New Regime 2020',
    period: 'Optional lower-rate route',
    points: [
      'Introduced section 115BAC for individuals and HUFs.',
      'Offered more slabs and lower headline rates but removed most deductions and exemptions.',
      'Best suited for users with fewer deductions, depending on salary structure.',
    ],
  },
  {
    title: 'New Regime 2023-2024',
    period: 'Default regime phase',
    points: [
      'New regime became default from the 2023 changes.',
      'Rebate support increased to Rs 7L taxable income under the new regime.',
      '2024 improved the same regime with Rs 75,000 standard deduction and wider middle slabs.',
    ],
  },
  {
    title: 'New Regime 2025-2026',
    period: 'Current calculator phase',
    points: [
      'Nil slab up to Rs 4L and 30% rate only above Rs 24L.',
      'Section 87A rebate up to Rs 12L taxable income for eligible resident individuals.',
      'Budget 2026 did not introduce a fresh slab change; it carried forward the same structure.',
    ],
  },
];

const sourceLinks = [
  {
    label: 'Finance Bill, 2020',
    href: 'https://www.indiabudget.gov.in/budget2020-21/doc/Finance_Bill.pdf',
  },
  {
    label: 'Finance Bill, 2023',
    href: 'https://www.indiabudget.gov.in/budget2023-24/doc/Finance_Bill.pdf',
  },
  {
    label: 'Finance Bill, 2024',
    href: 'https://www.indiabudget.gov.in/budget2024-25/doc/Finance_Bill.pdf',
  },
  {
    label: 'Finance Bill, 2025',
    href: 'https://www.indiabudget.gov.in/budget2025-26/doc/Finance_Bill.pdf',
  },
  {
    label: 'Finance Bill, 2026',
    href: 'https://www.indiabudget.gov.in/doc/Finance_Bill.pdf',
  },
  {
    label: 'PRS summary: Income-tax (No. 2) Bill, 2025',
    href: 'https://prsindia.org/billtrack/the-income-tax-no2-bill-2025',
  },
];

const badgeClass = (regime: string) => {
  if (regime === 'Old regime') return 'bg-amber-50 text-amber-800 border-amber-200';
  if (regime === 'New regime') return 'bg-blue-50 text-blue-800 border-blue-200';
  return 'bg-emerald-50 text-emerald-800 border-emerald-200';
};

export function TaxHistory() {
  return (
    <Layout>
      <Helmet>
        <title>Indian Income Tax Regime History | Old vs New Tax Changes</title>
        <meta
          name="description"
          content="Explore the history of Indian income tax regime changes for salaried taxpayers, including old regime updates, new regime launch, rebates, slabs, cess, and standard deductions."
        />
        <meta name="robots" content="index,follow" />
        <link rel="canonical" href="https://www.ctccalculator.in/tax-history" />
      </Helmet>

      <main className="mx-auto max-w-6xl px-3 py-4 md:px-4 md:py-8">
        <section className="mb-8 rounded-xl border border-slate-200 bg-white p-5 shadow-sm md:p-8">
          <div className="mb-5 flex items-center gap-3">
            <BookOpen className="h-8 w-8 text-slate-700" aria-hidden="true" />
            <div>
              <p className="text-sm font-semibold uppercase tracking-wide text-slate-500">
                Public archive
              </p>
              <h1 className="text-2xl font-bold text-slate-900 md:text-4xl">
                Indian Income Tax Regime History
              </h1>
            </div>
          </div>
          <p className="max-w-3xl text-base leading-7 text-slate-600">
            A historical reference for major salaried-tax changes that affect CTC and take-home
            calculations. Use it to understand how the old regime, new regime, rebates, standard
            deduction, cess, and surcharge rules evolved over time.
          </p>
        </section>

        <section className="mb-8 grid gap-4 md:grid-cols-3">
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-5">
            <ShieldCheck className="mb-3 h-6 w-6 text-slate-700" aria-hidden="true" />
            <h2 className="mb-2 text-lg font-semibold text-slate-900">Old Regime</h2>
            <p className="text-sm leading-6 text-slate-600">
              Deduction-heavy structure. It stayed available even after the new regime became the
              default.
            </p>
          </div>
          <div className="rounded-xl border border-blue-200 bg-blue-50 p-5">
            <CalendarDays className="mb-3 h-6 w-6 text-blue-700" aria-hidden="true" />
            <h2 className="mb-2 text-lg font-semibold text-slate-900">New Regime</h2>
            <p className="text-sm leading-6 text-slate-600">
              Introduced in 2020, made default in 2023, revised in 2024 and 2025.
            </p>
          </div>
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-5">
            <FileText className="mb-3 h-6 w-6 text-emerald-700" aria-hidden="true" />
            <h2 className="mb-2 text-lg font-semibold text-slate-900">Current Status</h2>
            <p className="text-sm leading-6 text-slate-600">
              Tax Year 2026-27 carries forward the 2025 new-regime slabs under the Income-tax Act,
              2025 framework.
            </p>
          </div>
        </section>

        <section className="mb-8 rounded-xl border border-slate-200 bg-white p-5 shadow-sm md:p-8">
          <h2 className="mb-6 text-2xl font-bold text-slate-900">Timeline</h2>
          <div className="space-y-5">
            {timeline.map((item) => (
              <article key={item.year} className="grid gap-4 rounded-lg border border-slate-200 p-4 md:grid-cols-[120px_1fr]">
                <div>
                  <div className="text-2xl font-bold text-slate-900">{item.year}</div>
                  <span className={`mt-2 inline-block rounded-full border px-3 py-1 text-xs font-semibold ${badgeClass(item.regime)}`}>
                    {item.regime}
                  </span>
                </div>
                <div>
                  <h3 className="mb-2 text-lg font-semibold text-slate-900">{item.label}</h3>
                  <ul className="list-disc space-y-2 pl-5 text-sm leading-6 text-slate-600">
                    {item.details.map((detail) => (
                      <li key={detail}>{detail}</li>
                    ))}
                  </ul>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="mb-8 rounded-xl border border-slate-200 bg-white p-5 shadow-sm md:p-8">
          <h2 className="mb-6 text-2xl font-bold text-slate-900">Regime Snapshots</h2>
          <div className="grid gap-5 md:grid-cols-2">
            {regimeSnapshots.map((snapshot) => (
              <article key={snapshot.title} className="rounded-lg border border-slate-200 p-4">
                <p className="mb-1 text-sm font-medium text-slate-500">{snapshot.period}</p>
                <h3 className="mb-3 text-xl font-semibold text-slate-900">{snapshot.title}</h3>
                <ul className="list-disc space-y-2 pl-5 text-sm leading-6 text-slate-600">
                  {snapshot.points.map((point) => (
                    <li key={point}>{point}</li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </section>

        <section className="rounded-xl border border-slate-200 bg-slate-50 p-5 md:p-8">
          <h2 className="mb-4 text-2xl font-bold text-slate-900">Source Trail</h2>
          <p className="mb-4 text-sm leading-6 text-slate-600">
            Source links are provided for auditability. This archive is not financial advice and may
            omit niche rules that do not affect ordinary salaried CTC calculations.
          </p>
          <ul className="grid gap-3 text-sm md:grid-cols-2">
            {sourceLinks.map((source) => (
              <li key={source.href}>
                <a
                  href={source.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-blue-700 underline-offset-4 hover:underline"
                >
                  {source.label}
                </a>
              </li>
            ))}
          </ul>
        </section>
      </main>

      <Footer />
    </Layout>
  );
}
